import React, { useEffect, useRef, useState } from 'react';
import { clearLastPath, getParameterByName } from '@shared/utils/UrlUtils';
import { useDispatch, useSelector } from 'react-redux';
import { redirectTo, selectCurrentUser, setUser } from '@shared/store/auth.duck';
import AuthWrapper from '../auth/components/AuthWrapper';
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { LoginWithRedirect } from "@shared/utils/AuthUtils";
import { Redirect } from "react-router";
import { bootstrapAuth, CALENDAR_CONSENT_PENDING_KEY } from '@shared/auth/authMachine.duck';
import { rawUserRequest } from '@shared/services/Auth';
import { updateOutlookSyncState } from '@features/settings/services/UserService';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { Trans, useTranslation } from "react-i18next";
import { isInTeams } from "@shared/utils/TeamsUtils";
import { TrackJS } from "trackjs";

export default function LoginWithAzure() {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const invitationId = getParameterByName('invitation', window.location.href);
  const {t} = useTranslation();
  const [userNotRegistered, setUserNotRegistered] = useState(false);

  const {instance, accounts, inProgress} = useMsal();
  const handledRef = useRef(false);

  useEffect(() => {
    document.body.style.backgroundColor = '#FAFAFA';
    clearLastPath();
  }, []);

  // MsalProvider calls handleRedirectPromise() internally.
  // Once inProgress settles to None the redirect response has been processed
  // and the account is available — acquire tokens silently from there.
  useEffect(() => {
    if (isInTeams()) return;
    if (inProgress !== InteractionStatus.None) return;
    if (handledRef.current) return;

    const account = instance.getActiveAccount() ?? accounts[0];
    if (account) {
      handledRef.current = true;
      instance.setActiveAccount(account);

      // If the user came from an invite-acceptance flow, bounce them back to
      // /invite?id=… so InviteLogin can complete the email-match + azureAcceptInvite
      // step. The cached MSAL account is now in place, so InviteLogin's resume
      // useEffect will fire on mount.
      const pendingInvite = localStorage.getItem('invite.pending');
      if (pendingInvite) {
        try {
          const { inviteId } = JSON.parse(pendingInvite);
          if (inviteId) {
            window.location.href = `/invite?id=${inviteId}`;
            return;
          }
        } catch {
          localStorage.removeItem('invite.pending');
        }
      }

      const handleErr = (err: any) => {
        const errMsg = err?.message || err?.toString?.() || '';
        if (errMsg.includes('User not registered')) {
          setUserNotRegistered(true);
        } else {
          TrackJS?.track(err);
        }
      };

      (async () => {
        try {
          await (dispatch(bootstrapAuth({ source: 'app-mount' })) as any).unwrap();
          const userResp = await rawUserRequest();
          dispatch(setUser(userResp));

          // If we just came back from a "Turn on calendar sync" redirect,
          // persist enableOutlookSync=true server-side now that the consent
          // is granted. The next page's auto-acquire effect will then fire
          // and silently pick up the calendar-scoped tokens.
          if (localStorage.getItem(CALENDAR_CONSENT_PENDING_KEY)) {
            localStorage.removeItem(CALENDAR_CONSENT_PENDING_KEY);
            try {
              await updateOutlookSyncState(true);
              dispatch(setUser({...currentUser, enableOutlookSync: true }));
            } catch (syncErr) {
              TrackJS?.track(`[LoginWithAzure] failed to enable outlook sync after consent redirect: ${syncErr}`);
            }
          }
        } catch (err) {
          handleErr(err);
        }
      })();
    }
  }, [instance, accounts, inProgress, dispatch]);

  useEffect(() => {
    if (isInTeams()) return;
    if (currentUser) {
      const redirectUrl = localStorage.getItem('redirect');
      localStorage.removeItem('redirect');
      if (redirectUrl === '/login/azure' || redirectUrl === '/login/google') {
        dispatch(redirectTo('/whereabouts/team'));
      } else {
        dispatch(redirectTo(redirectUrl ?? '/whereabouts/team'));
      }
    }
  }, [currentUser, dispatch]);

  const loginAgain = () => {
    LoginWithRedirect(instance, dispatch, true);
  }

  if (!!invitationId) {
    return (
      <Redirect to={`/invite?id=${invitationId}`} />
    )
  }

  if (userNotRegistered) {
    const email = instance.getActiveAccount()?.username ?? '';
    return (
      <AuthWrapper dataTest="login-not-registered" title="" spinner={false} hideLoginLink={true}>
        <BodyRegular style={{marginBottom: 16}}>
          {email ? t('login.not-registered', { email }) : t('login.not-registered-no-email')}
        </BodyRegular>
        <BodyRegular style={{marginBottom: 16}}>{t('login.not-registered-help')}</BodyRegular>
        <BodyRegular style={{marginBottom: 16}}><Trans i18nKey={'login.not-registered-assistance'} components={[<a href="mailto:hello@team-today.com">hello@team-today.com</a>]} /></BodyRegular>
        <PrimaryButton text={"register.title"}
                       click={() => dispatch(redirectTo('/register/azure'))}
                       fullWidth={true} />
      </AuthWrapper>
    )
  }

  return (
    <AuthWrapper dataTest="login-invite" title="" spinner={accounts.length > 0 || inProgress !== InteractionStatus.None} hideLoginLink={true}>
      <BodyRegular>{t('login.login-failed-with-azure')}</BodyRegular>
      <BodyRegular><Trans i18nKey={'login.login-failed-with-azure-help'} components={[<a href="mailto:hello@team-today.com">hello@team-today.com</a>]} /></BodyRegular>
      <PrimaryButton text={"button.try-again"}
                     click={loginAgain}
                     fullWidth={true} />
    </AuthWrapper>
  )
}
